import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { CurrencyAmountDisplay, plainAmount } from 'src/app/shared/utils/currency/currency-format';
import { Match, MatchType } from 'src/app/shared/types/match-type';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GameInterfaceState } from 'src/app/shared/models/game/game-interface-state';
import { GameId } from 'src/app/shared/models/game/game-id';
import { ThemePipe } from 'src/app/core/pipes/theme.pipe';
import { TimeFormatGhostPipe } from 'src/app/core/pipes/time-format-ghost.pipe';
import { TimeFormatPipe } from 'src/app/core/pipes/time-format.pipe';
import { IconComponent } from 'src/app/ui/icon/icon/icon.component';
import { ModePillComponent } from 'src/app/ui/pills/mode-pill/mode-pill.component';
import { CurrencyAmountComponent } from 'src/app/ui/currency/currency-amount/currency-amount.component';
import { GameTitleComponent } from 'src/app/ui/game-identity/game-title/game-title.component';
import { NavButtonComponent } from 'src/app/ui/buttons/nav-button/nav-button.component';
import { SoloTargetProgressComponent } from 'src/app/features/solo/components/solo-target-progress/solo-target-progress.component';
import type { SoloAmountDisplay } from 'src/app/features/solo/utils/solo-tiers.utils';
import { NgStyle, NgClass } from '@angular/common';

@Component({
  selector: 'app-game-score',
  templateUrl: './game-score.component.html',
  styleUrls: ['./game-score.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    NavButtonComponent,
    GameTitleComponent,
    NgStyle,
    CurrencyAmountComponent,
    ModePillComponent,
    SoloTargetProgressComponent,
    IconComponent,
    NgClass,
    TimeFormatPipe,
    TimeFormatGhostPipe,
    ThemePipe,
  ],
})
export class GameScoreComponent {
  readonly gameName = input<string>();
  // Labels the reward Local rather than Practice when the round has no prize to show.
  readonly isLocal = input(false);
  readonly gameId = input.required<GameId>();
  readonly gameInterfaceState = input.required<GameInterfaceState>();
  readonly score = input<number>();
  readonly reward = input<{
    primary: CurrencyAmountDisplay;
    secondary: CurrencyAmountDisplay;
  }>();
  readonly countdown = input<number>();
  readonly match = input<Match>();
  // A Solo round's target and prize, each undefined until its attempt is known.
  readonly soloTarget = input<number>();
  readonly soloPrize = input<SoloAmountDisplay>();

  readonly backClickedEvent = output<void>();
  readonly closeClickedEvent = output<void>();
  readonly cheatSheetClickedEvent = output<void>();

  // Which kind of match the header frames. Practice and local rounds count as duels: they show the
  // same prize pills, labelled with the round type instead of an amount.
  readonly mode = computed<MatchType>(() => this.match()?.matchType ?? 'duel');

  // Set only in `solo` mode, wrapped so the template can narrow it. Until the attempt is known the
  // header shows the Win pill alone: no bar and no prize rather than a wrong target or amount.
  readonly solo = computed(() =>
    this.mode() === 'solo' ? { target: this.soloTarget(), prize: this.soloPrize() } : undefined,
  );

  // Through the theme, as the Solo target beside it is, so a skin with number formatting off reads
  // the same digits in both pills.
  readonly formattedScore = computed(() => {
    const score = this.score();
    return score === undefined ? undefined : this.themeService.formatNumber(score);
  });

  // The prize as the header shows it: the reward's amount and icon, or the round type when the
  // reward has no amount text.
  readonly rewardAmount = computed<CurrencyAmountDisplay>(() => {
    const primary = this.reward()?.primary;
    const label = this.primaryRewardLabel;
    return primary ? { ...primary, text: label } : plainAmount(label);
  });

  themeService = inject(ThemeService);
  translocoService = inject(TranslocoService);

  GameInterfaceState = GameInterfaceState;

  readonly gameNameStyles: [string, string][] = [
    ['text-color', 'game-header-title'],
    ['type', 'game-header-title'],
  ];

  readonly eventTimerStyles: [string, string][] = [
    ['text-color', 'game-header-event-timer'],
    ['type', 'game-header-event-timer'],
  ];

  readonly eventCountdownStyles: [string, string][] = [
    ['text-color', 'game-header-event-countdown'],
    ['type', 'game-header-event-countdown'],
  ];

  readonly prizeStyles: [string, string][] = [
    ['text-color', 'game-header-prize'],
    ['type', 'game-header-prize'],
  ];

  // The score and timer pills read their own glow (game-header.pill.*, default none); their fill
  // and ring are in the SCSS (.game-pill--header), so the fill can fall back to pill-score on a
  // skin without the var. pill-score itself stays as it is: Blackjack and Video Poker read it in
  // Phaser, and the replay screen's pills (game-replay) keep using it.
  readonly scorePillStyles: [string, string][] = [
    ['drop-shadow', 'game-header-pill'],
    ['border-radius', 'pill-score'],
    ['text-color', 'pill-score-label'],
    ['type', 'pill-score-label'],
  ];

  // The duel inline prize pill keeps the plain pill-score look.
  readonly prizePillStyles: [string, string][] = [
    ['background', 'pill-score'],
    ['border-radius', 'pill-score'],
    ['text-color', 'pill-score-label'],
    ['type', 'pill-score-label'],
  ];

  readonly timerPillStyles: [string, string][] = [
    ['drop-shadow', 'game-header-pill'],
    ['border-radius', 'pill-score'],
    ['text-color', 'pill-score-label'],
    ['type', 'pill-score-label'],
    ['border', 'pill-countdown-danger'],
  ];

  readonly currencyPillSubLabelStyles: [string, string][] = [
    ['text-color', 'pill-currency-sub-label'],
    ['type', 'pill-currency-sub-label'],
  ];

  readonly scorePillSubLabelStyles: [string, string][] = [
    ['text-color', 'pill-currency-sub-label'],
    ['type', 'pill-currency-sub-label'],
  ];

  isEnded(): boolean {
    const countdown = this.countdown();
    return countdown !== undefined && countdown <= 0;
  }

  onBackClicked() {
    this.backClickedEvent.emit();
  }

  // The skin's close warning is the header's to show: it outlives this component, which the header
  // can hide while the warning is open.
  onCloseClicked() {
    this.closeClickedEvent.emit();
  }

  onCheatSheetClicked() {
    this.cheatSheetClickedEvent.emit();
  }

  get showScore(): boolean {
    return this.score() !== undefined && this.gameId() !== GameId.Marbles; // Score is hidden in Marbles, this is decision by design.
  }

  get primaryRewardLabel(): string {
    const reward = this.reward();
    if (reward?.primary.text) {
      return reward.primary.text;
    }

    return this.isLocal()
      ? this.translocoService.translate('pages.game-component.local')
      : this.translocoService.translate('pages.game-component.practice');
  }

  get tournamentLabel(): string {
    return this.translocoService.translate('components.tournament.tournament');
  }

  // The prize sits beside this label in the pill, from the round's attempt: a Solo room's reward is
  // always null. Must match app-solo-target-progress's Win pill, which takes over once the target
  // is known.
  get soloLabel(): string {
    return this.translocoService.translate('components.solo.win');
  }
}
